// Wire types for the AP REST API (`/api/v1`).
//
// Keys are the Postgres column names verbatim (api-contract.md §0.1), so one
// vocabulary runs from the database to the React component.
//
// PORT DECISION — money. The wire carries money as a decimal string
// ("1234.56") so no cent is ever lost to IEEE-754 drift. The app's arithmetic
// (the release gate, the line items footer, the accounts rollup) is the ported
// original and works in numbers, so the HTTP client parses money into `number`
// on the way in and formats it back to a decimal string on the way out. The
// conversion lives in exactly one file (`client.ts`); every type below is the
// app-facing shape, with money already numeric.
//
// ---------------------------------------------------------------------------
// AUTHORITATIVE TWIN. `shared/src/dto.ts` is now the generated server-side
// declaration of these same payloads, and where the two disagree, IT IS RIGHT —
// the backend serialises from it. This file is hand-written and predates it; it
// survives only because the app's money-as-number convention (above) and its
// `../lib/constants` enum imports do not exist on the shared side. A full swap
// is logged as tech debt, not attempted here: it needs the money layer moved and
// every `Money*` alias reconciled.
//
// Fixed 2026-08-04: the `oversight_approval` / `training` role tokens (neither
// exists) and all six `Analytics*` shapes, pinned by
// `mock/tests/fix-dashboard-parity.test.ts`.
//
// Fixed 2026-08-05: `AssociatedEntity` carried one overloaded `entity_id` where
// the API has TYPED columns (`vehicle_id` / `staff_upn`) and ignores unknown
// keys, so every hand-tagged Vehicle and Staff pick was silently discarded —
// pinned by `mock/tests/fix-associated-entity-wire.test.ts`. Also in that pass:
// `AccountSource` was missing `DocumentPrinted`; `Decision.company_id` is not
// serialised and is gone; `Invoice` gained `company_name_source`, `retired_at`
// and the two `ai_matching_*_tokens` counters; and `Profile` gained the
// covered-companies fields the catch-all expansion needs (§1.1).
//
// Drift still OPEN, and deliberate — do not assume the rest of this file is
// audited, but these three are decisions, not oversights:
//   * money is `number` here and `MoneyString` there, by design (see above), so
//     a naive type swap would break every arithmetic call site. Note `quantity`
//     is `numeric(14,4)`, not money: it is written with four decimals.
//   * `filters_applied` is optional here and required in the shared envelope.
//   * the shared DTOs spell nullable columns `T | null` where several types
//     below use `?: T | null`; absent and null are NOT the same thing on a
//     PATCH body (§0.7), so each one needs reading, not a blanket rewrite.
// ---------------------------------------------------------------------------

import type {
  AssignmentRole,
  DecisionOutcome,
  DecisionStep,
  InvoiceStatus,
  NodeKey,
  ReasonCategory,
  Workspace,
} from '../lib/constants'

// A handful of database enums have no UI-side constant to borrow a type from,
// so they are declared here. The values are the stored strings, verbatim
// (backend/db/schema.sql) — a mismatch is a wrong value, not a display quirk.
export type AccountSource =
  | 'Precedent'
  | 'DocumentPrinted'
  | 'VendorRule'
  | 'AiInferred'
  | 'Manual'
/** How "who is it for?" was answered. THE MODEL IS NEVER ONE OF THESE. */
export type CostCenterSource = 'ChartImplied' | 'VendorRule' | 'DocumentPrinted' | 'Manual'
export type CompanySource = 'UploaderDomain' | 'AiMatched' | 'Manual' | 'LineCompanies' | 'ScannerDefault'
/** How the company NAME was decided, separately from the company itself:
    `labeled` = printed on the document, `inferred` = worked out from it. */
export type CompanyNameSource = 'labeled' | 'inferred'
export type EntityType = 'Vehicle' | 'Staff' | 'Participant'
export type AssignmentAction = 'appoint' | 'retire'
export type AssignmentStatus = 'Pending' | 'Applied' | 'Rejected'
export type UploadStatusValue = 'Received' | 'Parsed' | 'Failed' | 'Rejected'
export type ExportStatusValue = 'Received' | 'Completed' | 'Failed'

/** Every mutable row carries an optimistic-concurrency counter (§0.6). */
export interface Versioned {
  row_version: number
}

export interface Page<T> {
  data: T[]
  next_cursor?: string | null
  total?: number
}

// ---------------------------------------------------------------------------
// Session / profile (§1.1)
// ---------------------------------------------------------------------------

/** Internal step roles — rows in `user_roles`. No company scope.
    Seven values, per api-contract §0.3. There is deliberately **no**
    `oversight_approval` role: Oversight is an appointment in `assignments`, and
    an active `oversight` row by itself carries the tier's authority and its
    document access. Company-SCOPED since 2026-08-10, like the other two tiers —
    the row no longer carries a NULL company and no longer sees every company. */
export type UserRoleToken =
  | 'import_control'
  | 'export_control'
  | 'closing_confirmation'
  | 'manual_entry'
  | 'denial_review'
  | 'references'
  | 'admin'

export interface ProfileAssignment {
  id: string
  workspace: string
  role: AssignmentRole
  /** NOT null for any live row since 2026-08-10 — `oversight` is company-scoped
      now, exactly like `director` and `department-head`. A `null` here can only
      be a retired, pre-2026-08-10 oversight row. */
  company_id: string | null
  company_name: string | null
  /** THE CATCH-ALL EXPANSION, per seat: the other companies this one seat also
      covers for DECIDING and for SEEING a record. Non-empty only for a seat on
      the `Other` anchor, where it is that anchor's member companies. It does NOT
      extend appointment authority, which stays company-exact. */
  covers_company_ids?: string[]
}

export interface ProfileCapabilities {
  /** True for internal roles/admin, or an active assignment in any of the three
      approval tiers — department-head, director, oversight alike (owner ruling
      R2, 2026-08-10: PDF preview stays granted to all three). */
  can_preview_pdf: boolean
  /** May the caller open the Assignments tab. Renamed from
      `can_manage_assignments`, read with a fallback below
      (`identity.ts`) while the server's compatibility shim sends both keys. */
  can_view_assignments: boolean
  /** @deprecated Compatibility shim for `can_view_assignments`. Same value. */
  can_manage_assignments?: boolean
  can_manage_references: boolean
  can_manage_fleet: boolean
  is_internal: boolean
}

export interface Profile {
  user_id: string
  upn: string
  display_name: string
  /** The address `conversations.mentions` stores — what the mentioned-you
      indicator compares against, not the UPN. */
  directory_email: string
  workspaces: string[]
  roles_by_workspace: Record<string, UserRoleToken[]>
  assignments: ProfileAssignment[]
  /** LITERAL — the companies the caller holds a director seat for, with no
      catch-all expansion. This is the APPOINTMENT scope, which the `Other`
      anchor does not widen (the 2026-08-03 broker ruling). */
  director_companies_by_workspace: Record<string, string[]>
  /** LITERAL, as above, for department-head seats. */
  department_companies_by_workspace: Record<string, string[]>
  /** LITERAL, as above, for `oversight` seats (issue 2, 2026-08-10). Oversight
      is a per-company assignment now, exactly like director and
      department-head, and appoints/retires within its own company only. */
  oversight_companies_by_workspace: Record<string, string[]>
  /** LITERAL appointment scope for `ap-ar` seats (issue 3, 2026-08-11). */
  ap_ar_companies_by_workspace: Record<string, string[]>
  /** EXPANDED — every company a director seat reaches for DECIDING and SEEING,
      the `Other` anchor's member companies included. This is the list a record's
      `company_id` is tested against before an approve control is offered;
      testing the literal list hides the control on a member company's invoice
      the API would accept. */
  director_covered_companies_by_workspace?: Record<string, string[]>
  /** EXPANDED, as above, for department-head seats. */
  department_covered_companies_by_workspace?: Record<string, string[]>
  /** EXPANDED, as above, for `oversight` seats. */
  oversight_covered_companies_by_workspace?: Record<string, string[]>
  /** EXPANDED deciding scope for `ap-ar` seats. Optional for the same reason the
      other three are: a payload from an older build carries only the literal. */
  ap_ar_covered_companies_by_workspace?: Record<string, string[]>
  visible_company_ids: string[]
  capabilities: ProfileCapabilities
}

// ---------------------------------------------------------------------------
// Queue counts (§2.1)
// ---------------------------------------------------------------------------

export interface QueueCounts {
  counts: Partial<Record<NodeKey, number>>
  as_of: string
}

// ---------------------------------------------------------------------------
// Stuck tiers (§2.2) — the Pipeline dialog's warning-icon diagnostic.
// ---------------------------------------------------------------------------

export interface StuckNodeCompany {
  company_id: string
  company_name: string
  count: number
}

export interface StuckNode {
  count: number
  companies: StuckNodeCompany[]
}

export interface QueueStuck {
  /** A tier with nothing stuck is omitted, not zeroed. */
  stuck: Partial<Record<string, StuckNode>>
  as_of: string
}

// ---------------------------------------------------------------------------
// Invoices (§3)
// ---------------------------------------------------------------------------

export interface Invoice extends Versioned {
  id: string
  external_invoice_id?: string | null
  upload_id?: string | null
  uploader_email?: string | null
  received_at?: string | null

  company_id?: string | null
  company_name?: string | null
  company_source?: CompanySource | null
  /** How the NAME was decided, separately from the company itself. */
  company_name_source?: CompanyNameSource | null
  company_name_as_printed?: string | null

  vendor_id?: string | null
  vendor_name?: string | null
  vendor_name_as_printed?: string | null

  invoice_date?: string | null
  subtotal?: number | null
  tax?: number | null
  total?: number | null
  currency?: string | null
  document_type?: string | null

  status: InvoiceStatus
  last_status_change_at?: string | null

  import_control_decision_id?: string | null
  department_approval_decision_id?: string | null
  director_approval_decision_id?: string | null
  oversight_approval_decision_id?: string | null
  export_control_decision_id?: string | null
  denial_review_decision_id?: string | null

  export_id?: string | null

  /** What the reviewer asked for at Import Control. */
  manual_entry_requested?: boolean | null
  /** The effective routing: the request OR a cross-company split. Derived. */
  manual_entry?: boolean | null
  line_items_capped?: boolean | null

  ai_extracting_confidence?: number | null
  ai_extracting_notes?: string | null
  ai_vendor_match_confidence?: number | null
  ai_company_match_confidence?: number | null
  ai_matching_notes?: string | null
  ai_matching_model?: string | null
  ai_matching_input_tokens?: number | null
  ai_matching_output_tokens?: number | null

  upload_page_start?: number | null
  upload_page_end?: number | null

  // derived, read-only
  unresolved_flag_count?: number | null
  total_flag_count?: number | null
  has_active_hold?: boolean | null
  flag_types?: string[] | null
  age_days?: number | null

  is_active?: boolean
  /** When the row was soft-deleted. Null on every live record. */
  retired_at?: string | null
  created_at?: string | null
  updated_at?: string | null
}

export interface InvoiceLineItem extends Versioned {
  id: string
  invoice_id: string
  line_number?: number | null
  vendor_id?: string | null
  company_id?: string | null
  company_name?: string | null
  /** The composed account. Read-only on the wire — Postgres generates it. */
  final_account_id?: string | null
  account_name?: string | null
  /** "What is it?" — on the document, settled at Import Control. */
  expense_type_code?: string | null
  expense_type_source?: AccountSource | null
  /** "Who is it for?" — never on the document, settled at approval or later. */
  cost_center_code?: string | null
  cost_center_source?: CostCenterSource | null
  printed_account_code?: string | null
  description?: string | null
  /** Written once at extraction. The GL-precedent key; never edited. */
  description_as_printed?: string | null
  quantity?: number | null
  unit_price?: number | null
  total?: number | null
  ai_extracting_confidence?: number | null
  ai_expense_type_match_confidence?: number | null
  ai_matching_notes?: string | null
  is_active?: boolean
  created_at?: string | null
  updated_at?: string | null
}

export interface Flag {
  id: string
  parent_id: string
  record_type: string
  flag_type: string
  detail?: string | null
  resolved_by_name?: string | null
  resolved_by_email?: string | null
  resolved_by_user_id?: string | null
  resolved_at?: string | null
  // joined from flag_definitions
  label?: string | null
  resolution?: 'auto' | 'explicit' | null
  description?: string | null
  is_active?: boolean
  created_at?: string | null
}

export interface FlagDefinition {
  flag_type: string
  label: string
  resolution: 'auto' | 'explicit'
  description: string
  workspaces: string[]
}

export interface Decision {
  id: string
  parent_id: string
  record_type: string
  decided_by_name?: string | null
  decided_by_email?: string | null
  decided_by_user_id?: string | null
  decided_at: string
  step: DecisionStep
  decision: DecisionOutcome
  reason?: string | null
  reason_category?: ReasonCategory | null
  // NO `company_id`. The column exists (it is the row's RLS partition key, set by
  // the Import Control release sweep) but §6.1 never serialises it, so a client
  // that read it would always read undefined.
}

export interface Hold {
  id: string
  parent_id: string
  record_type: string
  step?: DecisionStep | null
  held_by_name?: string | null
  held_by_email?: string | null
  held_by_user_id?: string | null
  held_at: string
  reason: string
  released_by_name?: string | null
  released_by_email?: string | null
  released_at?: string | null
  is_active?: boolean
}

export interface Correction {
  id: string
  parent_id: string
  record_type: string
  child_id?: string | null
  field: string
  old_value?: string | null
  new_value?: string | null
  old_source?: string | null
  vendor_id?: string | null
  vendor_name?: string | null
  corrected_by_name?: string | null
  corrected_by_email?: string | null
  corrected_at: string
}

export interface Conversation {
  id: string
  parent_id: string
  record_type: string
  body: string
  author_name: string
  author_email: string
  /** A real array — the source stored a semicolon-delimited string. */
  mentions: string[]
  created_at: string
}

export interface AssociatedEntity extends Versioned {
  id: string
  parent_id: string
  child_id?: string | null
  record_type: string
  entity_type: EntityType
  entity_source_text?: string | null
  entity_name?: string | null
  /** TYPED REFERENCE COLUMNS, not one overloaded tag (api-contract §9): a
      `Vehicle` row carries `vehicle_id`, a real foreign key to `vehicles.id`, and
      a `Staff` row carries `staff_upn`. A database CHECK permits only the column
      matching `entity_type`, and `Participant` permits neither — participant
      resolution was never built, so a participant tag is as-printed text only. */
  vehicle_id?: string | null
  staff_upn?: string | null
  match_source?: string | null
  is_active?: boolean
}

export interface Upload {
  id: string
  source?: string | null
  received_at?: string | null
  processed_at?: string | null
  status: UploadStatusValue
  record_count?: number | null
  record_type: string
  expected_type?: string | null
  ai_extracting_notes?: string | null
  file_name?: string | null
  object_key?: string | null
  file_url?: string | null
  rejection_code?: string | null
}

export interface ExportFile {
  file_id: string
  file_name: string
  company_id?: string | null
  row_count?: number | null
  download_url?: string | null
  expires_at?: string | null
}

export interface ExportBatch {
  id: string
  exported_by_name?: string | null
  exported_by_email?: string | null
  exported_at: string
  status: ExportStatusValue
  record_count?: number | null
  object_key?: string | null
  files?: ExportFile[]
}

export interface Assignment {
  id: string
  workspace: string
  action: AssignmentAction
  role: AssignmentRole
  company_id?: string | null
  company_name?: string | null
  user_id: string
  user_upn: string
  user_name?: string | null
  target_assignment_id?: string | null
  retired_at?: string | null
  request_status: AssignmentStatus
  rejection_reason?: string | null
  requested_by_name?: string | null
  requested_by_upn?: string | null
  created_at?: string | null
  is_active?: boolean
}

export interface UserRole {
  id: string
  user_object_id: string
  user_upn: string
  user_email: string
  user_name?: string | null
  workspace: string
  role: UserRoleToken
  is_active?: boolean
  created_at?: string | null
}

export interface ReminderSubscription extends Versioned {
  id: string
  subscriber_id: string
  subscriber_name?: string | null
  subscriber_email: string
  label?: string | null
  days_of_week: string[]
  start_date: string
  end_date?: string | null
  time_of_day: string
  workspaces: string[]
  steps: string[]
  status: 'active' | 'paused' | 'superseded' | 'cancelled'
  lineage_id: string
  superseded_at?: string | null
  last_sent_at?: string | null
}

// ---------------------------------------------------------------------------
// Reference (master) data (§15)
// ---------------------------------------------------------------------------

export interface Company extends Versioned {
  id: string
  company_id: string
  company_name: string
  /** Uploader domains that map to this company; edited as a list (app.md §6). */
  email_domains?: string[]
  is_active: boolean
}

export interface CompanyEmailDomain extends Versioned {
  id: string
  domain: string
  company_id: string
  company_name?: string | null
  is_active: boolean
}

/** Scanner From-display-name -> company: the FALLBACK company for documents a
    facility scanner emails in. Applied at match time only when the document
    itself resolves no company. */
export interface CompanyScanner extends Versioned {
  id: string
  /** Lowercased, trimmed — the match key. */
  scanner_name: string
  company_id: string
  company_name?: string | null
  is_active: boolean
}

/** An email address allowed to mail documents into intake — the gate is data,
    so admitting a sender is a row here, not a deploy. */
export interface IntakeSender extends Versioned {
  id: string
  /** Lowercased, trimmed. `name@example.com` (exact) or `@example.com` (whole domain). */
  address: string
  is_active: boolean
}

export interface Vendor extends Versioned {
  id: string
  vendor_id: string
  vendor_name: string
  address_1?: string | null
  address_2?: string | null
  address_3?: string | null
  city?: string | null
  state_or_province?: string | null
  postal_code?: string | null
  phone?: string | null
  fax?: string | null
  is_active: boolean
}

export interface Account extends Versioned {
  id: string
  account_id: string
  account_name: string
  is_active: boolean
}

/**
 * A standing coding decision for one vendor, optionally narrowed to lines whose
 * normalized description contains `description_scope`. Either half of an
 * account, or both.
 */
export interface VendorCodingRule extends Versioned {
  id: string
  vendor_id: string
  description_scope?: string | null
  expense_type_code?: string | null
  cost_center_code?: string | null
  source: 'Controller' | 'Learned'
  is_active: boolean
}

/** The leading segment of an account id — the department charged. */
export interface CostCenter extends Versioned {
  id: string
  cost_center_code: string
  label: string
  search_terms?: string | null
  is_active: boolean
}

/** The trailing two segments — what was bought. */
export interface ExpenseType extends Versioned {
  id: string
  expense_type_code: string
  label: string
  search_terms?: string | null
  is_active: boolean
}

export interface VendorAlias {
  id: string
  alias_name: string
  vendor_id: string
  vendor_name?: string | null
  source: string
  evidence?: string | null
  is_active: boolean
}

export interface Vehicle extends Versioned {
  id: string
  number: string
  vin?: string | null
  license_plate?: string | null
  make?: string | null
  model?: string | null
  /** Text, not a number — the owner's export can carry "2009/2010". */
  year?: string | null
  /** The fleet owner's own label, not a company FK. */
  company_label?: string | null
  department?: string | null
  status: string
  is_active: boolean
}

/** The nine fields the fleet spreadsheet carries, in the owner's column order. */
export const FLEET_FIELDS = [
  'number',
  'vin',
  'license_plate',
  'make',
  'model',
  'year',
  'company_label',
  'department',
  'status',
] as const
export type FleetField = (typeof FLEET_FIELDS)[number]

export const FLEET_FIELD_LABEL: Record<FleetField, string> = {
  number: 'Veh #',
  vin: 'VIN',
  license_plate: 'License Plate',
  make: 'Make',
  model: 'Model',
  year: 'Year',
  company_label: 'Company',
  department: 'Department',
  status: 'Status',
}

export interface FleetFieldChange {
  field: FleetField
  before: string | null
  after: string | null
}

export interface VehicleImportPreview {
  preview_token: string
  expires_at: string
  sheet_name: string
  header_row: number
  mapping: {
    by_field: Partial<Record<FleetField, number>>
    unmapped: string[]
    missing: FleetField[]
    deterministic: boolean
  }
  adds: { row: number; values: Partial<Record<FleetField, string | null>> }[]
  updates: { row: number; vehicle_id: string; number: string; changes: FleetFieldChange[] }[]
  unchanged_count: number
  absent: { vehicle_id: string; number: string }[]
  skipped: { row: number; reason: string }[]
  duplicates: { number: string; rows: number[] }[]
}

export interface VehicleImportResult {
  added: number
  updated: number
  unchanged: number
  absent_untouched: number
}

// ---------------------------------------------------------------------------
// Analytics (§16) — server-side aggregates, RLS-scoped
// ---------------------------------------------------------------------------

// These six mirror `shared/src/dto.ts` §16 (`Analytics*Response`) and the
// handlers in `backend/src/http/routes/analytics.ts`, field for field. The one
// deliberate difference: money arrives as a decimal string and `client.ts`
// parses it to a number here, because every other money field in this file is
// already a number by the time a screen sees it.
//
// Nothing derived is invented — the overview reports a breakdown BY STATUS and
// no in-flight tile, so the dashboard folds the tile out of the breakdown
// (metrics.ts) rather than expecting a field the API does not send.

export interface AnalyticsEnvelope {
  as_of: string
  filters_applied?: Record<string, unknown>
}

export interface StatusBucket {
  status: InvoiceStatus
  /** Parsed from the contract's decimal string. */
  total: number
  count: number
}

export interface AnalyticsOverview extends AnalyticsEnvelope {
  by_status: StatusBucket[]
  received_in_window: number
  terminal_in_window: number
  average_days_to_terminal: number | null
  unresolved_flag_count: number
}

/** One age band as the API defines it. The bands are the SERVER's, named in
    days; the app colours them by position and never hardcodes the thresholds.
    `max_days: null` is the open-ended top band. */
export interface AgingBand {
  label: string
  min_days: number
  max_days: number | null
  count: number
}

/** Aging is counts only — there is no money in this aggregate. */
export interface AgingNode {
  node: NodeKey
  buckets: AgingBand[]
}

export interface AnalyticsAging extends AnalyticsEnvelope {
  nodes: AgingNode[]
}

export interface AnalyticsThroughput extends AnalyticsEnvelope {
  weeks: { week_start: string; received: number; completed: number }[]
}

export interface AnalyticsDenials extends AnalyticsEnvelope {
  by_reason_category: { reason_category: string | null; count: number }[]
  by_step: { step: DecisionStep; count: number }[]
}

export interface SpendEntity {
  entity_type: EntityType
  /** The resolved identity where there is one (vehicle number, staff UPN), the
      captured name otherwise — what the per-line tag actually stores. */
  entity_key: string
  entity_name: string | null
  count: number
  total: number
}

export interface AnalyticsSpendByEntity extends AnalyticsEnvelope {
  entities: SpendEntity[]
}

export interface AnalyticsCorrections extends AnalyticsEnvelope {
  by_field: { field: string; count: number }[]
  by_vendor: { vendor_id: string | null; vendor_name: string | null; count: number }[]
  /** Grouped by **(field, old_source)** — `old_source` alone merges the two
      source enums, which share three values. */
  by_old_source: { field: string; old_source: string | null; count: number }[]
  /** Flags resolved with ZERO corrections, PER FLAG TYPE — the false-positive
      signal, and what the flag-precision panel draws. */
  flags_resolved_without_corrections: { flag_type: string; count: number }[]
}

// ---------------------------------------------------------------------------
// Request payloads
// ---------------------------------------------------------------------------

export interface InvoiceListParams {
  status?: InvoiceStatus[]
  company_id?: string[]
  vendor_id?: string[]
  date_from?: string
  date_to?: string
  total_min?: number
  total_max?: number
  export_id?: string
  upload_id?: string
  manual_entry?: boolean
  unresolved_flags?: boolean
  flag_type?: string[]
  q?: string
  since?: string
  sort?: 'invoice_date_asc' | 'invoice_date_desc' | 'last_status_change_at_desc' | 'total_desc'
  view?: 'queue' | 'archive' | 'full'
  limit?: number
  cursor?: string
}

export type InvoiceInclude =
  | 'line_items'
  | 'flags'
  | 'decisions'
  | 'holds'
  | 'associated_entities'
  | 'conversations'

export interface InvoiceBundle {
  invoice: Invoice
  line_items?: InvoiceLineItem[]
  flags?: Flag[]
  decisions?: Decision[]
  holds?: Hold[]
  associated_entities?: AssociatedEntity[]
  conversations?: Conversation[]
}

/** A PATCH-shaped write: absent means "leave alone", `null` means "clear". */
export type Clearable<T> = { [K in keyof T]?: T[K] | null }

export type InvoiceHeaderWrite = Clearable<{
  external_invoice_id: string
  vendor_id: string
  vendor_name: string
  company_id: string
  company_name: string
  invoice_date: string
  subtotal: number
  tax: number
  total: number
  currency: string
  document_type: string
  manual_entry_requested: boolean
}>

/**
 * THE EXPENSE TYPE ONLY. The intake save is Import Control's, and Import
 * Control does not write a cost center — the server refuses one.
 */
export type InvoiceLineItemWrite = Clearable<{
  id: string
  row_version: number
  line_number: number
  description: string
  expense_type_code: string
  company_id: string
  company_name: string
  quantity: number
  unit_price: number
  total: number
}>

export interface IntakeSaveRequest {
  row_version: number
  invoice: InvoiceHeaderWrite
  line_items?: { upsert?: InvoiceLineItemWrite[]; remove?: string[] }
  release?: boolean
}

export interface IntakeSaveResult extends InvoiceBundle {
  released_to?: InvoiceStatus | null
  corrections_written?: number
}

export interface DecisionRequest {
  invoice_id: string
  step: DecisionStep
  decision: DecisionOutcome
  reason?: string | null
  reason_category?: ReasonCategory | null
  row_version?: number
}

export interface DecisionResult {
  decision: Decision
  invoice: Invoice
  previous_status: InvoiceStatus
  new_status: InvoiceStatus
}

export interface DecisionBatchRequest {
  step: DecisionStep
  decision: DecisionOutcome
  reason?: string | null
  reason_category?: ReasonCategory | null
  invoice_ids: string[]
}

export interface DecisionBatchItem {
  invoice_id: string
  applied: boolean
  new_status?: InvoiceStatus
  error?: { code: string; message: string }
}

export interface DecisionBatchResult {
  results: DecisionBatchItem[]
  applied_count: number
  rejected_count: number
}

export interface DecisionListParams {
  since?: string
  decision?: DecisionOutcome[]
  step?: DecisionStep[]
  limit?: number
  cursor?: string
}

export interface AssignmentListParams {
  workspace?: Workspace
  role?: AssignmentRole
  company_id?: string
  active?: boolean
  user_id?: string
  limit?: number
  cursor?: string
}

export interface AssignmentRequest {
  workspace: Workspace
  action: AssignmentAction
  role: AssignmentRole
  company_id?: string | null
  user_id: string
  user_upn: string
  user_name?: string | null
  target_assignment_id?: string | null
}

export interface AssignmentResult {
  assignment: Assignment
  retired: Assignment | null
}

export interface ReminderWrite {
  label?: string | null
  days_of_week: string[]
  time_of_day: string
  start_date: string
  end_date?: string | null
  workspaces: string[]
  steps: string[]
}

export interface PresignRequest {
  file_name: string
  content_type: string
  byte_size: number
  expected_type?: string
}

export interface PresignResult {
  upload_id: string
  object_key: string
  url: string
  fields: Record<string, string>
  expires_at: string
}

export interface RegisterUploadRequest {
  upload_id: string
  object_key: string
  file_name: string
  expected_type?: string
}

export interface SignedUrl {
  url: string
  expires_at: string
  file_name?: string | null
}

export interface ExportResult {
  export: ExportBatch
  files: ExportFile[]
  invoices_advanced: number
}

export interface ExportDetail {
  export: ExportBatch
  files: ExportFile[]
  invoice_counts: Partial<Record<InvoiceStatus, number>>
}

export interface CorrectionListParams {
  parent_id?: string
  parent_ids?: string[]
  since?: string
  field?: string
  vendor_id?: string
  limit?: number
  cursor?: string
}

export interface ReferenceListParams {
  q?: string
  active?: boolean
  limit?: number
  cursor?: string
}

/** A person from the directory — the SPA reads Microsoft Graph directly
    (api-contract §0.11), so this is not an API DTO. */
export interface DirectoryUser {
  id: string
  display_name: string
  upn: string
  mail?: string | null
  job_title?: string | null
  department?: string | null
}

export interface AnalyticsParams {
  from?: string
  to?: string
  company_id?: string[]
  vendor_id?: string[]
  entity_type?: EntityType
}
